import { useQuery } from '@apollo/client/react/index.js';
import { GlassInput, normalizeServiceOrderStatus } from '@merritrac/shared/components';
import { AlertTriangle, CheckCircle2, Clock, FileText, Timer, Users } from 'lucide-react';
import { useState } from 'react';

import {
  GET_ACTIVE_TECHNICIAN_LOCATIONS,
  GET_DASHBOARD_STATS,
  GET_RECENT_ACTIVITY,
  GET_SERVICE_ORDERS,
} from '../../lib/graphql/operations.js';

import { ActivityFeed } from './ActivityFeed.js';
import { KPIStrip } from './KPIStrip.js';
import { OrderStatusChart } from './OrderStatusChart.js';
import { RecentOrdersTable } from './RecentOrdersTable.js';
import { TechnicianMap } from './TechnicianMap.js';

import type { ReactElement } from 'react';

/* ── GraphQL response types ── */

interface DashboardStats {
  activeOrders: number;
  completedToday: number;
  totalOrders: number;
  avgCompletionHours: number;
  activeTechnicians: number;
  pendingOrders: number;
  overdueOrders: number;
}

interface GetDashboardStatsData {
  dashboardStats: DashboardStats | null;
}

interface ActivityEvent {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  user: string;
}

interface GetRecentActivityData {
  recentActivity: ActivityEvent[];
}

interface ServiceOrderClient {
  clientCode: string;
  clientName: string | null;
  city: string | null;
  state: string | null;
}

interface ServiceOrderNode {
  accessKey: number;
  ticketno: string | null;
  description: string | null;
  status: string | null;
  priority: string | null;
  assignedTo: string | null;
  clientCode: string | null;
  createdDate: string | null;
  dueDate: string | null;
  completedDate: string | null;
  jobSite: string | null;
  client: ServiceOrderClient | null;
}

interface ServiceOrderEdge {
  node: ServiceOrderNode;
}

interface GetServiceOrdersData {
  serviceOrders: {
    totalCount: number;
    edges: ServiceOrderEdge[];
  };
}

interface TimeLocationNode {
  accessKey: number;
  user: string | null;
  clockInTime: string | null;
  clockOutTime: string | null;
  clockInLatitude: number | null;
  clockInLongitude: number | null;
  clockInAddress: string | null;
  clockOutLatitude: number | null;
  clockOutLongitude: number | null;
  clockOutAddress: string | null;
}

interface GetTimeLocationsData {
  timeLocations: {
    edges: { node: TimeLocationNode }[];
  };
}

function formatOrderDate(raw: string): string {
  if (!raw) {
    return '\u2014';
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) {
    return raw;
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function DashboardPage(): ReactElement {
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const { data: statsData, loading: statsLoading } = useQuery<GetDashboardStatsData>(
    GET_DASHBOARD_STATS,
    { variables: { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined } },
  );

  const { data: activityData, loading: activityLoading } = useQuery<GetRecentActivityData>(
    GET_RECENT_ACTIVITY,
    { variables: { limit: 15 } },
  );

  const {
    data: ordersData,
    loading: ordersLoading,
    error: ordersError,
  } = useQuery<GetServiceOrdersData>(GET_SERVICE_ORDERS, { variables: { first: 8 } });

  const { data: techData, loading: techLoading } = useQuery<GetTimeLocationsData>(
    GET_ACTIVE_TECHNICIAN_LOCATIONS,
  );

  const stats = statsData?.dashboardStats;
  const activities = activityData?.recentActivity ?? [];

  // Build KPI items
  const kpiItems = stats
    ? [
        {
          label: 'In Progress',
          value: stats.activeOrders,
          change: 'currently open',
          icon: FileText,
        },
        {
          label: 'Completed',
          value: stats.completedToday,
          change: 'total completed',
          icon: CheckCircle2,
          accentColor: 'var(--success)',
        },
        {
          label: 'Pending',
          value: stats.pendingOrders,
          change: 'awaiting action',
          icon: Clock,
        },
        {
          label: 'Overdue',
          value: stats.overdueOrders,
          change: 'past due',
          icon: AlertTriangle,
          highlight: stats.overdueOrders > 0,
        },
        {
          label: 'Technicians',
          value: stats.activeTechnicians,
          change: 'clocked in now',
          icon: Users,
          accentColor: 'var(--accent)',
        },
        {
          label: 'Avg. Time',
          value: stats.avgCompletionHours,
          suffix: 'd',
          decimals: 0,
          change: 'avg completion',
          icon: Timer,
        },
      ]
    : [];

  // Build chart data
  const pieData = stats
    ? [
        { name: 'In Progress', value: stats.activeOrders, color: '' },
        { name: 'Completed', value: stats.completedToday, color: '' },
        { name: 'Pending', value: stats.pendingOrders, color: '' },
        { name: 'Overdue', value: stats.overdueOrders, color: '' },
      ].filter((d) => d.value > 0)
    : [];

  // Build order rows
  const recentOrders = (ordersData?.serviceOrders.edges ?? []).map((e) => {
    const node = e.node;
    return {
      accessKey: node.accessKey,
      id: String(node.ticketno !== null && node.ticketno !== '' ? node.ticketno : node.accessKey),
      client: node.jobSite ?? node.client?.clientName ?? '\u2014',
      location:
        [node.client?.city, node.client?.state]
          .filter((v): v is string => v !== null && v !== undefined)
          .join(', ') || '\u2014',
      status: normalizeServiceOrderStatus(node.status),
      assignee: node.assignedTo ?? '',
      date: formatOrderDate(node.dueDate ?? node.createdDate ?? ''),
    };
  });

  // Build technician map data
  const techLocations = (techData?.timeLocations.edges ?? [])
    .filter((e) => e.node.clockInLatitude !== null && e.node.clockInLongitude !== null)
    .map((e) => ({
      accessKey: e.node.accessKey,
      user: e.node.user ?? 'Unknown',
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lat: e.node.clockInLatitude!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lng: e.node.clockInLongitude!,
      address: e.node.clockInAddress ?? '',
      isActive: e.node.clockOutTime === null,
    }));

  return (
    <div className="space-y-5">
      {/* Header + Date Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-text">Command Center</h1>
          <p className="mt-0.5 text-sm text-text-secondary">Real-time operations overview</p>
        </div>
        <div className="flex gap-3">
          <GlassInput
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
            }}
            className="w-36"
          />
          <GlassInput
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
            }}
            className="w-36"
          />
        </div>
      </div>

      {/* KPI Strip */}
      <KPIStrip items={kpiItems} loading={statsLoading} />

      {/* Map + Status Chart row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <TechnicianMap
          technicians={techLocations}
          jobs={[]}
          loading={techLoading}
          className="lg:col-span-3 relative"
        />
        <div className="lg:col-span-2">
          <OrderStatusChart data={pieData} total={stats?.totalOrders ?? 0} loading={statsLoading} />
        </div>
      </div>

      {/* Activity Feed + Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ActivityFeed activities={activities} loading={activityLoading} />
        <div className="lg:col-span-2">
          <RecentOrdersTable
            orders={recentOrders}
            loading={ordersLoading}
            error={ordersError?.message}
          />
        </div>
      </div>
    </div>
  );
}
